import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {convert_sha256, extractErrorMessage, goBack} from '../../../../common/common';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {VtnDto, VtnViewModel} from 'src/app/model/vtns.model';
import { CookieService } from 'ngx-cookie-service';
import { Subject, takeUntil } from 'rxjs';
import {VenMode} from "../../../../model/vens.model";

@Component({
  selector: 'app-create-vtn-index',
  standalone: true,
  imports: [RouterModule, CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './create-vtn.component.html',
  styleUrl: './create-vtn.component.scss'
})
export class CreateVtnComponent implements OnInit, OnDestroy {
  // User info
  public username: string;

  // Unsubscribe subject for cleanup
  private unsubscribe = new Subject<void>();

  // Form
  vtnForm = new FormGroup({
    vtn_id: new FormControl<string>('', [Validators.required]),
    uri: new FormControl<string>('', [Validators.required]),
    mode: new FormControl<VenMode>('poll', [Validators.required]),
    poll_failure_limit: new FormControl<number | undefined>(0),
    fingerprint: new FormControl<string>(''),
    ca: new FormControl<any>(null),
    single_ven_mode: new FormControl<boolean>(false),
  });
  protected vtnViewModel: VtnViewModel;
  protected vtnDto: VtnDto = {
    vtn_id: '',
    uri: '',
    mode: 'poll',
    single_ven_mode: false,
    poll_failure_limit: 0,
    fingerprint: '',
    ca: null
  };
  public editing: boolean = false;
  public id: number;
  public error: string | null = null;
  public caFingerprint: string = "No CA uploaded.";
  public vtnCertFingerprint: string = "";
  public vtnPublicCert: string = "";
  public router: Router;

  // Store original single_ven_mode value for edit mode
  private originalSingleVenMode: boolean = false;

  // Mode options for dropdown
  public modeOptions: { value: VenMode; label: string }[] = [
    { value: 'poll', label: 'Poll' },
    { value: 'push', label: 'Push' }
  ];

  constructor(
    private injector: Injector,
    private apiService: ApiService,
    private cookieService: CookieService
  ) {
    try {
      const loginInfo = JSON.parse(this.cookieService.get('oadr_login'));
      this.username = loginInfo.username;
    } catch (e) {
      this.username = '';
    }

    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.id = event.snapshot.params.id;
      }
    });
  }

  setFormsDefaultValue() {
    // Set all form values first
    this.vtnForm.controls.vtn_id.setValue(this.vtnDto?.vtn_id || null);
    this.vtnForm.controls.uri.setValue(this.vtnDto?.uri || null);
    this.vtnForm.controls.mode.setValue(this.vtnDto?.mode || 'poll', { emitEvent: false });
    this.vtnForm.controls.poll_failure_limit.setValue(this.vtnDto?.poll_failure_limit || null);
    this.vtnForm.controls.fingerprint.setValue(this.vtnDto?.fingerprint || '');
    this.vtnForm.controls.single_ven_mode.setValue(this.vtnDto?.single_ven_mode || false);

    // Store original value and disable mode control AFTER setting values
    if (this.editing) {
      this.originalSingleVenMode = this.vtnDto?.single_ven_mode || false;
      this.vtnForm.controls.mode.disable();
    }

    // Calculate and display fingerprint based on mode
    if (this.vtnDto?.mode === 'poll' && this.vtnDto?.ca) {
      // Poll mode: calculate CA fingerprint
      const fingerprints = convert_sha256(this.vtnDto.ca);
      this.caFingerprint = fingerprints.primary;
    } else if (this.vtnDto?.mode === 'push' && this.vtnDto?.fingerprint) {
      // Push mode: display the stored fingerprint (calculated from VTN cert)
      this.vtnCertFingerprint = this.vtnDto.fingerprint;
    }

    // Set up mode-dependent validation
    this.setupModeValidation();
  }

  ngOnInit(): void {
    // Subscribe to form changes first (only once)
    this.subscribeFormChange();

    // Then load VTN data
    this.findVtn();
  }

  private subscribeFormChange() {
    // Subscribe to form changes
    this.vtnForm.controls.vtn_id.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) {
          this.vtnDto.vtn_id = val.toString();
        }
      });

    this.vtnForm.controls.uri.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) {
          this.vtnDto.uri = val;
        }
      });

    this.vtnForm.controls.mode.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) {
          this.vtnDto.mode = val;
          // Only reset single_ven_mode when switching to poll mode
          // In push mode, preserve the existing value (especially important in edit mode)
          if (val === 'poll') {
            this.vtnDto.single_ven_mode = false;
            this.vtnForm.controls.single_ven_mode.setValue(false);
          }
          this.setupModeValidation();
        }
      });

    this.vtnForm.controls.poll_failure_limit.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) {
          this.vtnDto.poll_failure_limit = val;
        }
      });

    this.vtnForm.controls.fingerprint.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) {
          this.vtnDto.fingerprint = val;
        }
      });

    this.vtnForm.controls.single_ven_mode.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val !== null) {
          this.vtnDto.single_ven_mode = val;
        }
      });
  }

  private findVtn() {
    if (this.editing) {
      const queryParams: CustomHttpParams = {
        'id': this.id,
        'depths': JSON.stringify({
          'vens': 2
        })
      }
      this.apiService.getVtn(queryParams)
        .subscribe({
          next: (response: VtnViewModel[]) => {
            if (response && response.length > 0) {
              this.vtnViewModel = response[0];
              this.vtnDto = { ...response[0] };
              this.setFormsDefaultValue();
            } else {
              this.error = "Failed to load VTN data.";
            }
          },
          error: (err) => {
            console.error('Error loading VTN:', err);
            this.error = extractErrorMessage(err, "Failed to load VTN data.");
          }
        });
    } else {
      // Set default values for new VTN
      this.setFormsDefaultValue();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /**
   * Set up mode-dependent form validation based on the selected mode
   */
  setupModeValidation(): void {
    const mode = this.vtnForm.get('mode')?.value;
    const pollFailureLimitControl = this.vtnForm.get('poll_failure_limit');
    const fingerprintControl = this.vtnForm.get('fingerprint');

    if (mode === 'poll') {
      // Poll mode: fingerprint is not needed
      fingerprintControl?.clearValidators();
      fingerprintControl?.updateValueAndValidity();

      // Clear fingerprint value when switching to poll mode (only if not in edit mode during initialization)
      if (fingerprintControl?.value && !this.editing) {
        fingerprintControl.setValue('');
        this.vtnDto.fingerprint = '';
        this.vtnCertFingerprint = '';
        this.vtnPublicCert = '';
      }
    } else if (mode === 'push') {
      // Push mode: fingerprint is required only if we're creating a new VTN
      // or if we're editing and no fingerprint exists yet
      if (!this.editing || !this.vtnDto.fingerprint) {
        fingerprintControl?.setValidators([Validators.required]);
      } else {
        // In edit mode with existing fingerprint, it's optional (user can keep the existing one)
        fingerprintControl?.clearValidators();
      }
      fingerprintControl?.updateValueAndValidity();

      // Clear CA and poll_failure_limit when switching to push mode (only if not in edit mode during initialization)
      if (this.vtnDto?.ca && !this.editing) {
        this.vtnDto.ca = null;
        this.caFingerprint = "No CA uploaded.";
        // Also clear the file input if it exists
        const fileInput = document.getElementById('ca') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      }
      if (pollFailureLimitControl?.value !== null && pollFailureLimitControl?.value !== undefined && !this.editing) {
        pollFailureLimitControl.setValue(null);
        this.vtnDto.poll_failure_limit = undefined;
      }
    }

    // Mark the form as dirty to trigger validation display (only if not in edit mode during initialization)
    if (!this.editing) {
      this.vtnForm.markAsDirty();
    }
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  save() {
    // Update form validation before saving
    this.setupModeValidation();

    // Validate form
    if (this.vtnForm.invalid) {
      this.error = 'Please fill in all required fields correctly';
      return;
    }

    // Mode-specific validation - only require fingerprint for new VTNs or if editing without existing fingerprint
    if (this.vtnDto.mode === 'push' && !this.vtnDto.fingerprint) {
      this.error = 'Fingerprint is required for push mode. Please upload a VTN certificate.';
      return;
    }

    // Validate single_ven_mode change if in edit mode
    if (this.editing && !this.originalSingleVenMode && this.vtnDto.single_ven_mode) {
      // Changing from false to true - need to check VEN count
      const venCount = this.getVenCountForVtn();

      if (venCount > 1) {
        this.error = 'Cannot enable single VEN mode: this VTN has more than one VEN associated. Please ensure the VTN has one or fewer VENs before enabling this mode.';
        return;
      }
    }

    // Clear any previous errors
    this.error = null;

    if (this.editing) {
      this.apiService.editVtn(this.vtnDto).subscribe({
        next: () => {
          goBack('/vtns', this.router);
        },
        error: (err) => {
          console.error('Error updating VTN:', err);
          this.error = extractErrorMessage(err);
        }
      });
    } else {
      this.apiService.createVtn(this.vtnDto).subscribe({
        next: () => {
          goBack('/vtns', this.router);
        },
        error: (err) => {
          console.error('Error creating VTN:', err);
          this.error = extractErrorMessage(err);
        }
      });
    }
  }

  /**
   * Generalized certificate file picker handler
   * Handles both CA certificate (poll mode) and VTN public certificate (push mode)
   * @param event File input change event
   * @param certType Type of certificate: 'ca' for CA cert in poll mode, 'vtn' for VTN cert in push mode
   */
  onCertificatePicked(event: any, certType: 'ca' | 'vtn') {
    const mode = this.vtnForm.get('mode')?.value;

    // Validate certificate type matches current mode
    if (certType === 'ca' && mode === 'push') {
      this.error = "CA certificate upload is not available in Push mode.";
      return;
    }

    if (certType === 'vtn' && mode === 'poll') {
      this.error = "VTN certificate upload is only available in Push mode.";
      return;
    }

    const file = event?.target.files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.readAsText(file);

    reader.addEventListener(
      "load",
      () => {
        const certContent = reader.result as string;

        if (certType === 'ca') {
          // Handle CA certificate (poll mode)
          this.vtnDto.ca = certContent;
          const fingerprints = convert_sha256(certContent);
          this.caFingerprint = fingerprints.primary;
        } else {
          // Handle VTN public certificate (push mode)
          this.vtnPublicCert = certContent;
          const fingerprints = convert_sha256(certContent);
          this.vtnCertFingerprint = fingerprints.primary;

          // Store fingerprint in DTO and form for saving
          this.vtnDto.fingerprint = this.vtnCertFingerprint;
          this.vtnForm.controls.fingerprint.setValue(this.vtnCertFingerprint);
        }
      },
      false
    );

    reader.addEventListener(
      "error",
      () => {
        const certTypeName = certType === 'ca' ? 'CA' : 'VTN';
        this.error = `Error reading ${certTypeName} certificate file.`;
      },
      false
    );
  }

  get isPollMode(): boolean {
    return this.vtnForm.get('mode')?.value === 'poll';
  }

  get isPushMode(): boolean {
    return this.vtnForm.get('mode')?.value === 'push';
  }

  /**
   * Get the number of VENs associated with this VTN
   * For now, returns 0 to allow the functionality to work
   */
  private getVenCountForVtn(): number {
    return this.vtnViewModel?.vens?.length ?? 0;
  }

  /**
   * Check if single_ven_mode checkbox should be disabled
   * Disabled in poll mode, and in edit mode when changing from false to true
   * (the actual validation is done on save)
   */
  get isSingleVenModeDisabled(): boolean {
    return this.isPollMode;
  }
}
